import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  FaMapMarkerAlt,
  FaRegCalendarAlt,
  FaCar,
  FaMoneyBillWave,
  FaUserAlt,
  FaPhoneAlt,
  FaRegTimesCircle,
  FaArrowRight,
  FaRoute,
} from "react-icons/fa";

// Import shared components and services
import CancellationModal from "../components/shared/CancellationModal";
import RatingModal from "../components/shared/RatingModal";
import TripDetailsSummary from "../components/TripDetailsSummary";
import refundService from "../services/refundService";
import ratingService from "../services/ratingService";
import socketService from "../utils/socketService";
import { API_URL } from "../config/apiConfig";
import { getTimeFromSources, convertTo12HourFormat } from "../utils/timeUtils";
import { formatDistance, formatTravelTime } from "../utils/objectIdUtils";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentMethodBadgeClass,
  getPaymentStatusBadgeClass,
  getPaymentMethodIcon,
  getPaymentStatusIcon,
} from "../utils/paymentDisplayUtils";

// Utility function to format date in DD-MM-YYYY format
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "Not set";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return "Invalid date";
  }
};

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [rawBookingData, setRawBookingData] = useState(null);

  // Extract and decode query parameters for fallback data
  const queryParams = new URLSearchParams(window.location.search);

  // Helper function to detect service type
  const detectServiceType = (bookingData, from, to, service) => {
    // First check explicit serviceType from backend - most reliable
    if (bookingData?.serviceType && bookingData.serviceType !== "undefined") {
      return bookingData.serviceType;
    }

    // Explicit service parameter from URL (very reliable)
    if (service === "rental") return "rental";
    if (service === "airport") return "airport";
    if (service === "outstation") return "outstation";

    // Strong indicators for airport service
    if (bookingData?.airport || bookingData?.airportCode || bookingData?.transferDirection) {
      return "airport";
    }

    // Check URL params for airport indicators
    if (queryParams.get("airport") || queryParams.get("airportCode") || queryParams.get("direction")) {
      return "airport";
    }

    // Strong indicators for rental service
    if (bookingData?.packageName || bookingData?.rentalHours || bookingData?.rentalKm) {
      return "rental";
    }

    // Check URL params for rental indicators
    if (queryParams.get("packageName") || queryParams.get("rentalHours") || queryParams.get("rentalKm")) {
      return "rental";
    }

    // Clean and compare location strings
    const cleanLocation = (location) => {
      if (!location) return "";
      const str = location.toString().toLowerCase().trim();
      // Skip ObjectIds
      if (str.length === 24 && /^[0-9a-fA-F]{24}$/.test(str)) return "";
      return str;
    };

    const cleanFrom = cleanLocation(from);
    const cleanTo = cleanLocation(to);

    // Only consider it rental if same location AND has rental indicators
    if (cleanFrom && cleanTo && cleanFrom === cleanTo &&
        (queryParams.get("packageName") || queryParams.get("rentalHours") || queryParams.get("rentalKm"))) {
      return "rental";
    }

    // Different cities = outstation service
    if (cleanFrom && cleanTo && cleanFrom !== cleanTo) {
      return "outstation";
    }

    // Default to outstation
    return "outstation";
  };

  // Safely get and decode parameters with fallback
  const getDecodedParam = (param, fallbackParam = null) => {
    let value = queryParams.get(param);
    if (!value && fallbackParam) {
      value = queryParams.get(fallbackParam);
    }
    try {
      return value ? decodeURIComponent(value) : "";
    } catch (e) {
      return value || ""; // Return original if decoding fails
    }
  };

  // Helper to clean location strings and avoid ObjectIds
  const cleanLocationString = (location) => {
    if (!location || typeof location !== "string") return null;
    // Skip ObjectIds (24 character hex strings)
    if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location))
      return null;
    return location.trim() || null;
  };

  // Get addresses - prefer pickup/drop, fall back to from/to if needed
  const fromParam = getDecodedParam("from");
  const toParam = getDecodedParam("to");
  const pickupAddress = getDecodedParam("pickupAddress");
  const dropAddress = getDecodedParam("dropAddress");

  const paymentStatusParam = queryParams.get("paymentStatus");
  const paymentMethodParam = queryParams.get("paymentMethod");
  const methodParam = queryParams.get("method");
  const cabNameParam = queryParams.get("cabName");
  const amountParam = queryParams.get("amount");
  const dateParam = queryParams.get("date");
  const timeParam = queryParams.get("time");
  const pickupTimeParam = queryParams.get("pickupTime");
  const travelTimeParam = queryParams.get("travelTime");

  const distanceParam = queryParams.get("distance");
  const viaCitiesParam = queryParams.get("via");
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  // Get authentication state
  const { user: userInfo } = useSelector((state) => state.auth);

  useEffect(() => {}, [userInfo]);

  // Helper function to check if user has already rated the driver
  const hasUserRated = () => {
    if (!rawBookingData) {
      return false;
    }


    // Check the main userRating structure - this should be the primary check
    const userRating = rawBookingData.userRating;
    if (userRating && typeof userRating === 'object') {
      // Primary check for rating field (same logic as display)
      if (userRating.rating !== undefined && userRating.rating !== null && userRating.rating !== '') {
        return true;
      }

      // Check various possible rating field names as fallback
      const possibleRatingFields = ['stars', 'score', 'value', 'userRating'];

      for (const field of possibleRatingFields) {
        const ratingValue = userRating[field];
        if (ratingValue !== undefined &&
            ratingValue !== null &&
            ratingValue !== '' &&
            !isNaN(ratingValue) &&
            Number(ratingValue) >= 1 &&
            Number(ratingValue) <= 5) {
          return true;
        }
      }
    }

    // Check alternative rating structures as fallback
    if (rawBookingData.ratings &&
        rawBookingData.ratings.user &&
        rawBookingData.ratings.user.rating &&
        !isNaN(rawBookingData.ratings.user.rating) &&
        Number(rawBookingData.ratings.user.rating) >= 1 &&
        Number(rawBookingData.ratings.user.rating) <= 5) {
      return true;
    }

    return false;
  };

  // Helper function to check if we should stop polling (for incomplete rating objects)
  const shouldStopPolling = () => {
    if (!rawBookingData || !rawBookingData.userRating) {
      return false; // Continue polling if no rating object exists
    }

    const userRating = rawBookingData.userRating;

    // If rating object exists but only has createdAt (no actual rating), check age
    if (userRating.createdAt && !userRating.rating) {
      const createdAt = new Date(userRating.createdAt);
      const now = new Date();
      const ageInMinutes = (now - createdAt) / (1000 * 60);

      // Stop polling if incomplete rating is older than 10 minutes
      if (ageInMinutes > 10) {
        return true;
      }
    }

    return false;
  };

  // Initialize socket connection for real-time updates
  useEffect(() => {
    if (userInfo && userInfo.id) {
      // Connect to socket server
      socketService.connect();

      // Join user room to receive updates
      socketService.joinUserRoom(userInfo.id);

      // Listen for ride completion events
      socketService.onRideCompleted((data) => {
        if (booking && (
          data.bookingId === booking.id ||
          data._id === id ||
          data.bookingId === id ||
          (rawBookingData && data.bookingId === rawBookingData._id)
        )) {
          toast.success("Your ride has been completed!");
          fetchBookingDetails(); // Refresh booking data

          // Show rating modal automatically after a short delay, only if user hasn't rated yet
          setTimeout(() => {
            if (!hasUserRated()) {
              setShowRatingModal(true);
            }
          }, 2000);
        }
      });

      // Listen for rating submission events
      socketService.onRatingSubmitted((data) => {

        if (booking && (
          data.bookingId === booking.id ||
          data._id === id ||
          data.bookingId === id ||
          (rawBookingData && data.bookingId === rawBookingData._id)
        )) {
          if (data.ratingType === 'driverRating') {
            toast.info("Driver has rated your ride");
          } else {
            toast.info("A new rating has been submitted for your ride");
          }
          fetchBookingDetails();
        }
      });

      // Listen for driver rating updates specifically
      socketService.onDriverRatingUpdate((data) => {
        if (booking && (
          data.bookingId === booking.id ||
          data._id === id ||
          data.bookingId === id ||
          (rawBookingData && data.bookingId === rawBookingData._id)
        )) {
          toast.info("Driver has rated your ride");
          fetchBookingDetails();
        }
      });
    }

    return () => {
      // Disconnect socket when component unmounts
      socketService.disconnect();
    };
  }, [userInfo, id, booking]);

  // Define fetchBookingDetails function outside useEffect to make it accessible throughout the component
  const fetchBookingDetails = async () => {
    if (!id) {
      setError("No booking ID provided");
      setIsLoading(false);
      return;
    }

    try {
      // Check if the ID is a booking ID (starts with CB), local booking, or a MongoDB ID
      let endpoint;
      if (id.startsWith("CB")) {
        endpoint = `${API_URL}/booking/track/${id}`;
      } else if (id.startsWith("local-")) {
        // Local bookings are typically stored in localStorage or handled differently
        // Try to get booking data from localStorage first
        const localBookings = JSON.parse(
          localStorage.getItem("bookings") || "[]",
        );
        const localBooking = localBookings.find((booking) => booking.id === id);

        if (localBooking) {
          // Use local booking data
          setBooking({
            id: localBooking.id,
            date: formatDateDDMMYYYY(localBooking.date || new Date()),
            time: localBooking.time || new Date().toLocaleTimeString(),
            from: localBooking.from || fromParam || "Unknown",
            to: localBooking.to || toParam || "Unknown",
            cabType: localBooking.cabName || "Standard Cab",
            price: `���${localBooking.amount || 0}`,
            paymentMethod:
              localBooking.paymentMethod === "cod"
                ? "Cash on Delivery"
                : "Online Payment",
            paymentStatus: localBooking.paymentStatus || "Pending",
            paymentId: localBooking.paymentId || null,
            status: localBooking.status || "Confirmed",
            driverName: "To be assigned",
            driverContact: "To be provided",
            driverVehicle: "Not assigned",
            driverLicense: "Not provided",
            canRate: false,
            canCancel: false,
            isCompleted: false,
            startOtp: null, // Local bookings don't have OTP
          });
          setIsLoading(false);
          return;
        } else {
          // If not found in localStorage, try to use URL parameters as fallback
          console.log(
            "Local booking not found in storage, using URL parameters",
          );
          setBooking({
            id: id.startsWith("CB") ? id : `CB${id.slice(-8).toUpperCase()}`,
            date: formatDateDDMMYYYY(new Date()),
            time: timeParam
              ? decodeURIComponent(timeParam)
              : new Date().toLocaleTimeString(),
            from: fromParam || "Unknown location",
            to: toParam || "Unknown destination",
            cabType: cabNameParam
              ? decodeURIComponent(cabNameParam)
              : "Standard Cab",
            price: `₹${amountParam || 0}`,
            paymentMethod:
              paymentMethodParam === "cod"
                ? "Cash on Delivery"
                : "Online Payment",
            paymentStatus: paymentStatusParam
              ? paymentStatusParam.charAt(0).toUpperCase() +
                paymentStatusParam.slice(1)
              : "Pending",
            status: "Pending",
            driverName: "To be assigned",
            driverContact: "To be provided",
            driverVehicle: "Not assigned",
            driverLicense: "Not provided",
            canRate: false,
            canCancel: false,
            isCompleted: false,
            startOtp: null, // URL fallback bookings don't have OTP
          });
          setIsLoading(false);
          return;
        }
      } else {
        endpoint = `${API_URL}/booking/${id}`;
      }

      // Set up headers with auth token if available
      const config = {};

      // Check if user is logged in and has a valid token
      if (userInfo && userInfo.token) {
        config.headers = {
          Authorization: `Bearer ${userInfo.token}`,
        };
      } else {
        console.log(
          "No authentication token available - retrieving user from localStorage",
        );
        // Try to get token from localStorage as fallback
        const localUser = JSON.parse(localStorage.getItem("user"));
        if (localUser && localUser.token) {
          config.headers = {
            Authorization: `Bearer ${localUser.token}`,
          };
        } else {
          // If this is not a public booking tracking route, show appropriate message
          if (!id.startsWith("CB")) {
            setError("Authentication required to view booking details");
            setIsLoading(false);
            toast.error("Please log in to view this booking");
            navigate("/login", { state: { from: `/booking/${id}` } });
            return;
          }
          // For tracking IDs (CB prefixed), we'll try without authentication
        }
      }

      const response = await axios.get(endpoint, config);

      console.log("🌐 API Response received:", {
        endpoint,
        success: response.data.success,
        hasData: !!response.data.data,
        fullResponse: response.data
      });

      if (response.data.success) {
        const bookingData = response.data.data;

        console.log("📦 Raw booking data from API:", {
          bookingId: bookingData._id,
          driverInfo: {
            adminDriver: bookingData.driver,
            vendorDriver: bookingData.vendorDriver,
            driverSource: bookingData.driverSource,
            vendorId: bookingData.vendorId,
            hasAdminDriver: !!bookingData.driver,
            hasVendorDriver: !!bookingData.vendorDriver,
            adminDriverName: bookingData.driver?.name,
            vendorDriverName: bookingData.vendorDriver?.name,
            legacyDriverName: bookingData.driverName,
            legacyDriverContact: bookingData.driverContact
          },
          driverRating: bookingData.driverRating,
          userRating: bookingData.userRating,
          userRatingDetailed: {
            exists: !!bookingData.userRating,
            keys: bookingData.userRating ? Object.keys(bookingData.userRating) : [],
            stringified: JSON.stringify(bookingData.userRating),
            ratingValue: bookingData.userRating?.rating,
            ratingType: typeof bookingData.userRating?.rating,
            hasRatingProperty: bookingData.userRating?.hasOwnProperty('rating'),
            allProperties: bookingData.userRating ? Object.entries(bookingData.userRating) : []
          },
          ratings: bookingData.ratings,
          status: bookingData.status,
          hasDriverRating: !!bookingData.driverRating,
          hasUserRating: !!bookingData.userRating,
          driverRatingStructure: bookingData.driverRating ? Object.keys(bookingData.driverRating) : null,
          userRatingStructure: bookingData.userRating ? Object.keys(bookingData.userRating) : null
        });

        // Enhanced location extraction with better fallbacks
        const extractLocationString = (location, fallback) => {
          if (!location) return fallback || "Location not specified";

          // If it's a string, check if it's an ObjectId first
          if (typeof location === "string") {
            // Check if it's an ObjectId (24 character hex string)
            if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location)) {
              return fallback || "Location not specified";
            }
            return location.trim() || fallback || "Location not specified";
          }

          // If it's an object, try to extract meaningful information
          if (typeof location === "object") {
            // Check for address fields in order of preference
            if (
              location.address &&
              typeof location.address === "string" &&
              location.address.trim()
            ) {
              return location.address.trim();
            }
            if (
              location.name &&
              typeof location.name === "string" &&
              location.name.trim()
            ) {
              return location.name.trim();
            }
            if (location.city || location.state) {
              return [location.city, location.state]
                .filter(Boolean)
                .join(", ")
                .trim();
            }
            if (location.coordinates && Array.isArray(location.coordinates)) {
              return "Custom Location";
            }
          }

          return fallback || "Location not specified";
        };

        // Helper function to extract location name from ObjectId or object
        const extractLocationName = (location, fallback) => {
          if (!location) return fallback;

          // If it's a string, check if it's an ObjectId
          if (typeof location === "string") {
            // Check if it's an ObjectId (24 character hex string)
            if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location)) {
              return fallback; // Return fallback for ObjectId
            }
            return location;
          }

          // If it's an object, try to extract name or address
          if (typeof location === "object") {
            return (
              location.name || location.address || location.city || fallback
            );
          }

          return fallback;
        };

        const fromLocation =
          bookingData.pickupLocation || bookingData.pickup || bookingData.from;
        const toLocation =
          bookingData.dropLocation || bookingData.drop || bookingData.to;

        // Enhanced location extraction with better fallbacks
        console.log("Available address data:", {
          urlPickup: pickupAddress,
          urlDrop: dropAddress,
          dbPickup: bookingData?.pickupAddress,
          dbDrop: bookingData?.dropAddress,
          fromParam,
          toParam,
          dbFrom: bookingData?.from,
          dbTo: bookingData?.to,
        });

        // Helper to validate addresses
        const isAddressValid = (addr) => {
          if (!addr || typeof addr !== "string") return false;
          const trimmed = addr.trim();
          if (!trimmed) return false;

          // List of values that are definitely not addresses
          const invalidValues = [
            "unknown",
            "na",
            "n/a",
            "undefined",
            "null",
            "pickup",
            "drop",
            "address",
            "location",
            "city",
            "state",
            "select",
            "choose",
            "enter",
            "your",
            "please",
            "click",
          ];

          // Common patterns that indicate this is a real address
          const addressIndicators = [
            /\d+[a-z]?\s+[\w\s]+/i, // Number followed by text (e.g., "123 Main")
            /(st(reet)?|ave(nue)?|rd|road|ln|lane|dr(ive)?|blvd|way|hwy|highway)\b/i,
            /(ap(t|ar)?t|suite|unit|#|no\.?)[\s-]?[\w-]+/i,
            /(near|opposite|behind|beside|next to)\s+[\w\s]+/i,
            /[\w\s]+(colony|nagar|vihar|sector|phase|block|ward|area|chowk|circle)/i,
          ];

          // Check for invalid parts
          const hasInvalidParts = invalidValues.some((invalid) =>
            new RegExp(`\\b${invalid}\\b`, "i").test(trimmed),
          );

          // Check for address-like patterns
          const isLikelyAddress = addressIndicators.some((pattern) =>
            pattern.test(trimmed),
          );

          // Allow any non-empty string that doesn't have invalid parts
          return !hasInvalidParts && (isLikelyAddress || trimmed.length > 10);
        };

        // --- Simplified Address Logic ---
        const pickupOptions = [
          extractLocationName(bookingData.from, fromParam),
          fromParam,
          bookingData.pickupAddress,
          extractLocationString(bookingData.pickupLocation, ""),
        ].filter(Boolean);

        const dropoffOptions = [
          extractLocationName(bookingData.to, toParam),
          toParam,
          bookingData.dropAddress,
          extractLocationString(bookingData.dropLocation, ""),
        ].filter(Boolean);

        // Find the first valid, descriptive string from the options
        const finalPickup =
          pickupOptions.find(isAddressValid) || "Pickup Location";
        const finalDropoff =
          dropoffOptions.find(isAddressValid) || "Drop-off Location";

        // Store both general and detailed addresses separately for display
        const pickupGeneral =
          extractLocationName(bookingData.from, fromParam) ||
          fromParam ||
          extractLocationString(bookingData.pickupLocation, "") ||
          "Pickup Location";
        const pickupDetailed =
          cleanLocationString(bookingData.pickupAddress) ||
          cleanLocationString(fromParam) || // Use fromParam if it contains detailed address
          "No detailed address available";
        const dropoffGeneral =
          extractLocationName(bookingData.to, toParam) ||
          toParam ||
          extractLocationString(bookingData.dropLocation, "") ||
          "Drop-off Location";
        const dropoffDetailed =
          cleanLocationString(bookingData.dropAddress) ||
          cleanLocationString(toParam) || // Use toParam if it contains detailed address
          "No detailed address available";

        // Get cab type name
        let cabTypeName = "Standard Cab";
        if (bookingData.cabType && typeof bookingData.cabType === "object") {
          cabTypeName = bookingData.cabType.name || "Standard Cab";
        } else if (typeof bookingData.cabType === "string") {
          // Check if it's an ObjectId (24 character hex string)
          if (
            bookingData.cabType.length === 24 &&
            /^[0-9a-fA-F]{24}$/.test(bookingData.cabType)
          ) {
            cabTypeName = "Standard Cab"; // Default name for ObjectId
          } else {
            cabTypeName = bookingData.cabType || "Standard Cab";
          }
        }

        // Process driver information (handle both admin and vendor drivers)
        let driverName = "To be assigned";
        let driverContact = "To be provided";
        let driverVehicle = "";
        let driverLicense = "";

        // Check for both admin and vendor drivers
        const hasAdminDriver = bookingData.driver;
        const hasVendorDriver = bookingData.vendorDriver;
        const driverSource = bookingData.driverSource;

        console.log('🔍 Driver detection debug:', {
          hasAdminDriver: !!hasAdminDriver,
          hasVendorDriver: !!hasVendorDriver,
          driverSource,
          adminDriverType: typeof hasAdminDriver,
          vendorDriverType: typeof hasVendorDriver,
          adminDriverData: hasAdminDriver,
          vendorDriverData: hasVendorDriver
        });

        // Determine which driver to use based on driverSource or presence
        let activeDriver = null;
        let isVendorDriver = false;

        if (driverSource === "vendor" && hasVendorDriver) {
          activeDriver = hasVendorDriver;
          isVendorDriver = true;
          console.log('✅ Using vendor driver:', activeDriver);
        } else if (driverSource === "admin" && hasAdminDriver) {
          activeDriver = hasAdminDriver;
          isVendorDriver = false;
          console.log('✅ Using admin driver:', activeDriver);
        } else if (hasVendorDriver && !hasAdminDriver) {
          // Fallback: if only vendor driver exists
          activeDriver = hasVendorDriver;
          isVendorDriver = true;
          console.log('✅ Fallback to vendor driver:', activeDriver);
        } else if (hasAdminDriver && !hasVendorDriver) {
          // Fallback: if only admin driver exists
          activeDriver = hasAdminDriver;
          isVendorDriver = false;
          console.log('✅ Fallback to admin driver:', activeDriver);
        }

        // Display driver information if a driver exists, regardless of booking status
        if (activeDriver) {
          // Check if driver is a populated object with data
          if (
            typeof activeDriver === "object" &&
            activeDriver !== null
          ) {
            console.log(`${isVendorDriver ? 'Vendor' : 'Admin'} driver data:`, activeDriver);

            // Format the driver name - handle both formats (name or firstName/lastName)
            if (activeDriver.name) {
              driverName = activeDriver.name;
            } else if (
              activeDriver.firstName ||
              activeDriver.lastName
            ) {
              driverName =
                `${activeDriver.firstName || ""} ${activeDriver.lastName || ""}`.trim();
            } else {
              driverName = isVendorDriver ? "Vendor Driver" : "Driver"; // Fallback name
            }

            // Get contact information
            driverContact = activeDriver.phone || activeDriver.mobile || activeDriver.phoneNumber || "Contact not provided";
          } else if (
            typeof activeDriver === "string" &&
            activeDriver.length === 24 &&
            /^[0-9a-fA-F]{24}$/.test(activeDriver)
          ) {
            // If driver is just an ObjectId string, show appropriate message
            console.log(`${isVendorDriver ? 'Vendor' : 'Admin'} driver ObjectId (not populated):`, activeDriver);
            driverName = isVendorDriver ? "Vendor driver assigned" : "Driver assigned";
            driverContact = "Contact details will be provided soon";
            driverVehicle = "Vehicle details will be provided soon";
            driverLicense = "License details will be provided soon";

            // Get vehicle details directly from driver object
            // First check for direct vehicle properties
            if (
              activeDriver.vehicleModel ||
              activeDriver.vehicleNumber
            ) {
              const model = activeDriver.vehicleModel || "";
              const regNum = activeDriver.vehicleNumber || "Unknown";
              driverVehicle = `${model} (${regNum})`.trim();
              if (driverVehicle === "()")
                driverVehicle = "Vehicle details not available";
            }
            // Fall back to vehicleDetails if available
            else if (activeDriver.vehicleDetails) {
              const make = activeDriver.vehicleDetails.make || "";
              const model = activeDriver.vehicleDetails.model || "";
              const regNum =
                activeDriver.vehicleDetails.registrationNumber ||
                "Unknown";
              driverVehicle = `${make} ${model} (${regNum})`.trim();
              if (driverVehicle === "()")
                driverVehicle = "Vehicle details not available";
            } else {
              driverVehicle = "Vehicle details not available";
            }

            // Get license number
            driverLicense = activeDriver.licenseNumber || "Not provided";

            // Store driver info separately for potential additional use
            setDriverInfo(activeDriver);
          } else if (
            typeof activeDriver === "string" &&
            activeDriver &&
            !(
              activeDriver.length === 24 &&
              /^[0-9a-fA-F]{24}$/.test(activeDriver)
            )
          ) {
            // If driver is a non-ObjectId string, use it as name
            console.log(`${isVendorDriver ? 'Vendor' : 'Admin'} driver string (not ObjectId):`, activeDriver);
            driverName = activeDriver;
            driverContact = "Contact information will be provided";
          }
        }

        // Store raw booking data for later use with modals
        setRawBookingData(bookingData);

        // Process rating data

        // Format the final booking object for display
        setBooking({
          id:
            bookingData.bookingId ||
            bookingData.customBookingId ||
            (bookingData._id
              ? `CB${bookingData._id.slice(-8).toUpperCase()}`
              : id),
          date: formatDateDDMMYYYY(
            bookingData.travelDate ||
              bookingData.pickupDate ||
              dateParam ||
              new Date(),
          ),
          time: (() => {
            // Debug time detection
            console.log("Time debug:", {
              dbTravelTime: bookingData.travelTime,
              dbPickupTime: bookingData.pickupTime,
              timeParam: timeParam,
              pickupTimeParam: pickupTimeParam,
              travelTimeParam: travelTimeParam,
              dateParam: dateParam,
              fullBookingData: bookingData,
            });

            // Priority order for time detection
            const timeOptions = [
              bookingData.pickupTime, // Database pickup time
              bookingData.travelTime, // Database travel time
              pickupTimeParam, // URL pickup time parameter
              travelTimeParam, // URL travel time parameter
              timeParam, // URL time parameter
            ];

            // Find first valid time
            for (const timeOption of timeOptions) {
              if (timeOption && timeOption !== "Time not specified") {
                // Convert Date objects to time strings
                if (timeOption instanceof Date) {
                  return timeOption.toLocaleTimeString();
                }
                // Ensure it's a string and has content
                if (typeof timeOption === "string" && timeOption.trim()) {
                  return timeOption.trim();
                }
              }
            }

            return "Time not specified";
          })(),
          pickupTime: getTimeFromSources({
            pickupTime: bookingData.pickupTime,
            timeParam: timeParam,
            pickupTimeParam: pickupTimeParam,
            travelTimeParam: travelTimeParam,
          }),
          from: finalPickup,
          to: finalDropoff,
          dbFrom:
            cleanLocationString(
              extractLocationName(bookingData.from, fromParam),
            ) ||
            cleanLocationString(fromParam) ||
            "Pickup Location",
          dbTo:
            cleanLocationString(extractLocationName(bookingData.to, toParam)) ||
            cleanLocationString(toParam) ||
            "Drop-off Location",
          pickupAddress:
            pickupDetailed !== "No detailed address available"
              ? pickupDetailed
              : null,
          dropAddress:
            dropoffDetailed !== "No detailed address available"
              ? dropoffDetailed
              : null,
          pickupAddressCity: pickupGeneral,
          dropAddressCity: dropoffGeneral,
          cabType: (() => {
            // Use the processed cab type name, but also check URL params
            let finalCabType =
              cabTypeName !== "Standard Cab"
                ? cabTypeName
                : cabNameParam || "Standard Cab";

            // If cabNameParam exists and is decoded, prefer it
            if (cabNameParam) {
              try {
                const decodedCabName = decodeURIComponent(cabNameParam);
                if (decodedCabName && decodedCabName !== "undefined") {
                  finalCabType = decodedCabName;
                }
              } catch (e) {
                // Use cabNameParam as is if decoding fails
                finalCabType = cabNameParam;
              }
            }

            return finalCabType;
          })(),
          price: `₹${bookingData.totalAmount || amountParam || 0}`,
          distance: parseFloat(bookingData.distance || distanceParam || 0),
          ...(bookingData.duration
            ? { duration: `${bookingData.duration} hours` }
            : {}),
          status: bookingData.status || "Pending",
          driverName: driverName,
          driverContact: driverContact,
          driverVehicle: driverVehicle,
          driverLicense: driverLicense,
          paymentMethod: (() => {
            // Debug payment method detection
            console.log("Payment method debug:", {
              dbPaymentMethod: bookingData.paymentMethod,
              paymentMethodParam: paymentMethodParam,
              methodParam: methodParam,
              paymentStatus: bookingData.paymentStatus,
              status: bookingData.status,
              fullBookingData: bookingData,
            });

            // First check the actual database payment method
            if (bookingData.paymentMethod) {
              return bookingData.paymentMethod; // This should be 'cod' for cash payments
            }

            // Check URL parameters
            const method = paymentMethodParam || methodParam || "";
            if (method) {
              const methodLower = method.toLowerCase();
              if (methodLower.includes("cod") || methodLower.includes("cash")) {
                return "cod";
              }
              return method;
            }

            // Check payment status for cash indicators
            const status =
              bookingData.paymentStatus || paymentStatusParam || "";
            if (status && status.toLowerCase().includes("cash")) {
              return "cod";
            }

            // Check if payment status is pending and amount > 0 (might indicate COD)
            if (
              bookingData.paymentStatus === "pending" &&
              bookingData.totalAmount > 0
            ) {
              // If it's pending with an amount, likely COD
              return "cod";
            }

            // Default fallback
            return "online";
          })(),
          paymentStatus: bookingData.paymentStatus
            ? bookingData.paymentStatus.charAt(0).toUpperCase() +
              bookingData.paymentStatus.slice(1)
            : paymentStatusParam
              ? paymentStatusParam.charAt(0).toUpperCase() +
                paymentStatusParam.slice(1)
              : "Pending",
          paymentId: bookingData.paymentId || null,
          canRate: bookingData.status === "completed" && !hasUserRated(),
          canCancel: ["pending", "confirmed", "accepted"].includes(
            bookingData.status,
          ),
          isCompleted: bookingData.status === "completed",
          // Trip type information (One Way vs Round Trip vs Multi-City)
          tripType: (() => {
            // Check multiple sources for trip type
            const dbJourneyType = bookingData.journeyType;
            const dbTripType = bookingData.tripType;
            const urlTripType = queryParams.get("tripType");
            const urlType = queryParams.get("type");
            const viaCitiesParam = queryParams.get("via");

            // Check journey type first (primary field)
            if (dbJourneyType && dbJourneyType !== "undefined") {
              if (dbJourneyType === "roundTrip") {
                return "roundTrip";
              }
              if (dbJourneyType === "oneWay") {
                return "oneWay";
              }
              // Support legacy formats if any exist
              if (dbJourneyType === "round_trip" || dbJourneyType === "round-trip") {
                return "roundTrip";
              }
              if (dbJourneyType === "one_way" || dbJourneyType === "one-way") {
                return "oneWay";
              }
            }

            // Return first available value, with proper fallback
            if (dbTripType && dbTripType !== "undefined") return dbTripType;
            if (urlTripType) return urlTripType;
            if (urlType) {
              // Convert common variations to standard format
              if (urlType === "round-trip" || urlType === "roundTrip") return "roundTrip";
              if (urlType === "one-way" || urlType === "oneWay") return "oneWay";
              return urlType;
            }

            // Check for round trip indicators
            if (bookingData.returnDate || bookingData.returnTime || queryParams.get("returnDate")) {
              return "roundTrip";
            }

            // Check for via cities (multi-city trip)
            if (viaCitiesParam || (bookingData.viaCities && bookingData.viaCities.length > 0)) {
              return "multiCity";
            }

            return "oneWay"; // Default fallback
          })(),
          returnDate:
            bookingData.returnDate || queryParams.get("returnDate") || null,
          returnTime:
            bookingData.returnTime || queryParams.get("returnTime") || null,

          // Via cities information - check both database and URL params
          viaCities: (() => {
            // First check if booking has via cities
            if (bookingData.viaCities && Array.isArray(bookingData.viaCities) && bookingData.viaCities.length > 0) {
              return bookingData.viaCities;
            }

            // Check URL parameter for via cities
            if (viaCitiesParam) {
              return viaCitiesParam.split(',').map(city => city.trim()).filter(city => city.length > 0);
            }

            // Check if via cities are stored as a string and need to be parsed
            if (bookingData.viaCitiesString) {
              return bookingData.viaCitiesString.split(',').map(city => city.trim()).filter(city => city.length > 0);
            }

            return [];
          })(),

          // Service type information (improved detection)
          serviceType: detectServiceType(
            bookingData,
            fromParam,
            toParam,
            queryParams.get("service")
          ),
          packageName:
            bookingData.packageName ||
            (queryParams.get("packageName")
              ? decodeURIComponent(queryParams.get("packageName"))
              : null),
          rentalHours:
            bookingData.rentalHours ||
            (queryParams.get("rentalHours")
              ? Number(queryParams.get("rentalHours"))
              : null),
          rentalKm:
            bookingData.rentalKm ||
            (queryParams.get("rentalKm")
              ? Number(queryParams.get("rentalKm"))
              : null),
          // Trip start OTP for customer display
          startOtp: bookingData.startOtp || null,
        });

        setIsLoading(false);
      } else {
        setError(response.data.message || "Failed to fetch booking details");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);

      // If API fails, try to use URL parameters as fallback
      if (fromParam && amountParam) {
        console.log("API failed, using URL parameters as fallback");
        setBooking({
          id: id.startsWith("CB") ? id : `CB${id.slice(-8).toUpperCase()}`,
          date: formatDateDDMMYYYY(new Date()),
          time: timeParam
            ? decodeURIComponent(timeParam)
            : new Date().toLocaleTimeString(),
          from: fromParam || "Unknown location",
          to: toParam || "Unknown destination",
          cabType: cabNameParam
            ? decodeURIComponent(cabNameParam)
            : "Standard Cab",
          price: `��������${amountParam || 0}`,
          paymentMethod:
            paymentMethodParam === "cod"
              ? "Cash on Delivery"
              : "Online Payment",
          paymentStatus: paymentStatusParam
            ? paymentStatusParam.charAt(0).toUpperCase() +
              paymentStatusParam.slice(1)
            : "Pending",
          status: "Confirmed",
          driverName: "To be assigned",
          driverContact: "To be provided",
          driverVehicle: "Not assigned",
          driverLicense: "Not provided",
          canRate: false,
          canCancel: false,
          isCompleted: false,
          distance: parseFloat(distanceParam || 0),

          // Trip type information (fallback)
          tripType: (() => {
            const urlTripType = queryParams.get("tripType");
            const urlType = queryParams.get("type");
            const viaCitiesParam = queryParams.get("via");

            if (urlTripType) return urlTripType;
            if (urlType) {
              // Convert common variations to standard format
              if (urlType === "round-trip" || urlType === "roundTrip") return "roundTrip";
              if (urlType === "one-way" || urlType === "oneWay") return "oneWay";
              return urlType;
            }

            // Check for round trip indicators
            if (queryParams.get("returnDate")) {
              return "roundTrip";
            }

            // Check for via cities (multi-city trip)
            if (viaCitiesParam) {
              return "multiCity";
            }

            return "oneWay"; // Default fallback
          })(),
          returnDate: queryParams.get("returnDate") || null,
          returnTime: queryParams.get("returnTime") || null,

          // Via cities information from URL params
          viaCities: viaCitiesParam
            ? viaCitiesParam.split(',').map(city => city.trim()).filter(city => city.length > 0)
            : [],

          // Service type information (from URL params if available)
          serviceType: detectServiceType(
            null, // No booking data in fallback
            fromParam,
            toParam,
            queryParams.get("service")
          ),
          packageName: queryParams.get("packageName")
            ? decodeURIComponent(queryParams.get("packageName"))
            : null,
          rentalHours: queryParams.get("rentalHours")
            ? Number(queryParams.get("rentalHours"))
            : null,
          rentalKm: queryParams.get("rentalKm")
            ? Number(queryParams.get("rentalKm"))
            : null,
          // No OTP available in fallback mode
          startOtp: null,
        });
        setIsLoading(false);
      } else {
        setError(
          "Error fetching booking details: " +
            (error.response?.data?.message || error.message),
        );
        setIsLoading(false);
      }
    }
  };

  // Get current time in 24-hour format (HH:MM)
  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const mins = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${mins}`;
  }, []);

  // Use an effect to fetch booking details and set up socket listeners
  useEffect(() => {
    // Call the fetchBookingDetails function
    fetchBookingDetails();

    // Initialize socket connection for real-time updates
    if (userInfo && userInfo.id) {
      socketService.connect();
      socketService.joinUserRoom(userInfo.id);

      // Listen for booking update events
      socketService.listenForBookingUpdates((updatedBooking) => {
        console.log("Received booking update:", updatedBooking);
        if (updatedBooking._id === id || updatedBooking.bookingId === id) {
          toast.info("Booking information has been updated");
          fetchBookingDetails(); // Refresh the booking data
        }
      });

      // Listen for driver location updates
      socketService.listenForDriverLocationUpdates((location) => {
        console.log("Received driver location update:", location);
        // You could update a map component here if implemented
      });

      // Listen for payment/refund events
      socketService.listenForPaymentUpdates((paymentUpdate) => {
        console.log("Received payment update:", paymentUpdate);
        if (
          paymentUpdate.bookingId === id ||
          (rawBookingData && paymentUpdate.bookingId === rawBookingData._id)
        ) {
          toast.success("Refund has been processed for this booking");
          fetchBookingDetails(); // Refresh the booking data
        }
      });

      // Clean up socket listeners when component unmounts
      return () => {
        socketService.disconnect();
      };
    }
  }, [id, userInfo]); // Re-run effect when id or userInfo changes

  // Polling effect for real-time driver rating updates on completed rides
  useEffect(() => {
    let intervalId;

    // Only start polling if ride is completed, we have booking data, user hasn't rated yet, and we shouldn't stop
    if (booking && booking.status === "completed" && !hasUserRated() && !shouldStopPolling()) {
      console.log("🔄 Starting rating polling for completed ride:", booking.id);

      intervalId = setInterval(() => {
        console.log("🔄 Polling for rating updates...");

        // Check if user has rated or we should stop polling before making API call
        if (hasUserRated()) {
          console.log("✅ Rating detected - stopping polling");
          clearInterval(intervalId);
          return;
        }

        if (shouldStopPolling()) {
          console.log("⚠️ Stopping polling - should stop polling condition met");
          clearInterval(intervalId);
          return;
        }

        fetchBookingDetails();
      }, 5000); // Poll every 5 seconds (reduced frequency)
    } else if (booking && booking.status === "completed" && hasUserRated()) {
      console.log("✅ User has already rated - skipping polling");
    } else if (booking && booking.status === "completed" && shouldStopPolling()) {
      console.log("⚠️ User rating incomplete but stale - skipping polling");
    }

    // Cleanup interval on unmount or when conditions change
    return () => {
      if (intervalId) {
        console.log("🔄 Stopping rating polling");
        clearInterval(intervalId);
      }
    };
  }, [booking?.status, rawBookingData]); // Re-run when booking status or booking data changes

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">
            <FaRegTimesCircle />
          </div>
          <h2 className="text-2xl font-bold mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Handle booking cancellation
  const handleCancelBooking = async (cancelData) => {
    try {
      setIsCancelling(true);

      if (!userInfo || !userInfo.token) {
        toast.error("You must be logged in to cancel a booking");
        return;
      }

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const response = await axios.put(
        `${API_URL}/booking/${rawBookingData._id}/cancel`,
        {
          reason: cancelData.reason,
          isRefundEligible: cancelData.isRefundEligible,
        },
        config,
      );

      if (response.data.success) {
        toast.success("Booking cancelled successfully");
        fetchBookingDetails(); // Refresh booking data
      } else {
        toast.error(response.data.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error(error.response?.data?.message || "Failed to cancel booking");
    } finally {
      setIsCancelling(false);
      setShowCancellationModal(false);
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    try {
      if (!userInfo || !userInfo.token) {
        toast.error("You must be logged in to download invoice");
        return;
      }

      if (!rawBookingData || !rawBookingData._id) {
        toast.error("Booking information not available");
        return;
      }

      // Generate and download the invoice
      const invoiceData = generateInvoiceHTML();

      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      printWindow.document.write(invoiceData);
      printWindow.document.close();

      // Wait for content to load then trigger print
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };

    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error("Failed to generate invoice");
    }
  };

  // Generate HTML content for invoice
  const generateInvoiceHTML = () => {
    const invoiceDate = new Date().toLocaleDateString("en-IN");
    const bookingDate = new Date(
      rawBookingData.createdAt || rawBookingData.pickupDate,
    ).toLocaleDateString("en-IN");

    // Get address and distance parameters for accurate display
    const fromLocation = booking.from || fromParam || 'Pickup Location';
    const toLocation = booking.to || toParam || 'Drop-off Location';
    const pickupAddr = booking.pickupAddress || pickupAddress;
    const dropAddr = booking.dropAddress || dropAddress;
    const actualDistance = rawBookingData?.distance || booking.distance || distanceParam;

    // Debug: Log pricing data available
    console.log("💰 Invoice pricing data:", {
      rawBookingData,
      baseAmount: rawBookingData.baseAmount,
      taxAmount: rawBookingData.taxAmount,
      tollCharges: rawBookingData.tollCharges,
      nightCharges: rawBookingData.nightCharges,
      driverAllowance: rawBookingData.driverAllowance,
      totalAmount: rawBookingData.totalAmount,
      bookingPrice: booking.price
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>UrbanRide Invoice - ${booking.id}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; line-height: 1.6; }
          .invoice-container { max-width: 800px; margin: 20px auto; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f59e0b; padding-bottom: 20px; }
          .logo { font-size: 2.5rem; font-weight: bold; color: #f59e0b; margin-bottom: 10px; }
          .company-details { color: #666; }
          .invoice-title { font-size: 1.8rem; margin: 30px 0; text-align: center; color: #1f2937; }
          .invoice-info { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-details, .billing-details { background: #f8fafc; padding: 20px; border-radius: 8px; width: 48%; }
          .section-title { font-weight: bold; color: #374151; margin-bottom: 15px; font-size: 1.1rem; }
          .info-row { margin-bottom: 8px; }
          .label { font-weight: 600; color: #4b5563; }
          .trip-details { background: #e0f2fe; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .route { display: flex; align-items: center; margin: 15px 0; }
          .route-icon { width: 20px; height: 20px; margin-right: 10px; }
          .amount-section { background: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #bbf7d0; }
          .amount-row { display: flex; justify-content: space-between; margin-bottom: 12px; padding: 5px 0; }
          .amount-row:nth-child(even) { background: rgba(255,255,255,0.5); padding: 8px; border-radius: 4px; }
          .total-amount { font-size: 1.4rem; font-weight: bold; border-top: 3px solid #10b981; padding-top: 15px; margin-top: 15px; background: #dcfce7; padding: 15px; border-radius: 8px; color: #166534; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; }
          @media print {
            body { -webkit-print-color-adjust: exact; }
            .invoice-container { margin: 0; padding: 0; max-width: none; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="logo">UrbanRide</div>
            <div class="company-details">
              <p><strong>Premium Cab Services</strong></p>
              <p>Email: info@urbanridetech.com | Phone: +91 7096366636</p>
            </div>
          </div>

          <div class="invoice-title">TAX INVOICE</div>

          <div class="invoice-info">
            <div class="invoice-details">
              <div class="section-title">Invoice Details</div>
              <div class="info-row"><span class="label">Invoice No:</span> INV-${booking.id}</div>
              <div class="info-row"><span class="label">Invoice Date:</span> ${invoiceDate}</div>
              <div class="info-row"><span class="label">Booking ID:</span> ${booking.id}</div>
              <div class="info-row"><span class="label">Booking Date:</span> ${bookingDate}</div>
            </div>

            <div class="billing-details">
              <div class="section-title">Billing To</div>
              <div class="info-row"><span class="label">Customer:</span> ${rawBookingData.user?.name || rawBookingData.passengerDetails?.name || "Customer"}</div>
              <div class="info-row"><span class="label">Phone:</span> ${rawBookingData.user?.phone || rawBookingData.passengerDetails?.phone || "N/A"}</div>
              <div class="info-row"><span class="label">Email:</span> ${rawBookingData.user?.email || rawBookingData.passengerDetails?.email || rawBookingData.email || "N/A"}</div>
            </div>
          </div>

          <div class="trip-details">
            <div class="section-title">Route Information</div>
            <div class="route" style="margin-bottom: 15px;">
              <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                <span style="color: #10b981; font-weight: bold; margin-right: 8px;">📍</span>
                <span><strong>Pickup:</strong></span>
              </div>
              <div style="margin-left: 25px; line-height: 1.6;">
                <div style="font-weight: 500; color: #374151; margin-bottom: 4px;">${fromLocation}</div>
                ${pickupAddr && pickupAddr !== fromLocation && pickupAddr.trim() ? `<div style="color: #6b7280; font-size: 0.9rem;">${pickupAddr}</div>` : ''}
              </div>
            </div>
            ${booking.serviceType !== 'rental' ? `
            <div class="route" style="margin-bottom: 15px;">
              <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
                <span style="color: #ef4444; font-weight: bold; margin-right: 8px;">📍</span>
                <span><strong>Drop-off:</strong></span>
              </div>
              <div style="margin-left: 25px; line-height: 1.6;">
                <div style="font-weight: 500; color: #374151; margin-bottom: 4px;">${toLocation}</div>
                ${dropAddr && dropAddr !== toLocation && dropAddr.trim() ? `<div style="color: #6b7280; font-size: 0.9rem;">${dropAddr}</div>` : ''}
              </div>
            </div>
            ` : ''}
            <div class="info-row"><span class="label">Date & Time:</span> ${booking.date}, ${booking.time}</div>
            <div class="info-row"><span class="label">Cab Type:</span> ${booking.cabType}</div>
            <div class="info-row"><span class="label">Distance:</span> ${actualDistance ? `${actualDistance} km` : 'Distance not available'}</div>
            ${booking.serviceType ? `<div class="info-row"><span class="label">Service Type:</span> ${booking.serviceType === 'rental' ? 'Local Rental' : booking.serviceType === 'airport' ? 'Airport Transfer' : 'Outstation'}</div>` : ''}
            ${booking.tripType && booking.serviceType === 'outstation' ? `<div class="info-row"><span class="label">Trip Type:</span> ${booking.tripType === 'roundTrip' ? 'Round Trip' : booking.tripType === 'multiCity' ? 'Multi-City' : 'One Way'}</div>` : ''}
            ${booking.viaCities && booking.viaCities.length > 0 ? `<div class="info-row"><span class="label">Via Cities:</span> ${booking.viaCities.join(', ')}</div>` : ''}
            ${booking.packageName ? `<div class="info-row"><span class="label">Package:</span> ${booking.packageName}</div>` : ''}
            ${booking.rentalHours ? `<div class="info-row"><span class="label">Duration:</span> ${booking.rentalHours} hours</div>` : ''}
            ${booking.rentalKm ? `<div class="info-row"><span class="label">Included Distance:</span> ${booking.rentalKm} km</div>` : ''}
            <div class="info-row"><span class="label">Driver:</span> ${booking.driverName}</div>
            ${booking.driverContact && booking.driverContact !== 'To be provided' ? `<div class="info-row"><span class="label">Driver Contact:</span> ${booking.driverContact}</div>` : ''}
            ${booking.driverVehicle && booking.driverVehicle !== 'Not assigned' ? `<div class="info-row"><span class="label">Vehicle:</span> ${booking.driverVehicle}</div>` : ''}
          </div>

          <div class="amount-section">
            <div class="section-title">Price Breakdown</div>
            ${(() => {
              // Get detailed pricing from booking data or calculate if not available
              const rawTotal = parseFloat(booking.price.replace('₹', '').replace(',', '')) || 0;
              const baseAmount = rawBookingData.baseAmount || Math.round(rawTotal / 1.18);
              const taxAmount = rawBookingData.taxAmount || (rawTotal - baseAmount);
              const tollCharges = rawBookingData.tollCharges || 0;
              const nightCharges = rawBookingData.nightCharges || 0;
              const driverAllowance = rawBookingData.driverAllowance || 0;
              const totalAmount = rawBookingData.totalAmount || rawTotal;

              // Calculate GST percentage
              const gstPercentage = baseAmount > 0 ? Math.round((taxAmount / baseAmount) * 100) : 18;

              let priceBreakdown = `
                <div class="amount-row">
                  <span>Base Fare</span>
                  <span>₹${baseAmount.toLocaleString("en-IN")}</span>
                </div>`;

              if (tollCharges > 0) {
                priceBreakdown += `
                <div class="amount-row">
                  <span>Toll Charges</span>
                  <span>₹${tollCharges.toLocaleString("en-IN")}</span>
                </div>`;
              }

              if (nightCharges > 0) {
                priceBreakdown += `
                <div class="amount-row">
                  <span>Night Charges</span>
                  <span>₹${nightCharges.toLocaleString("en-IN")}</span>
                </div>`;
              }

              if (driverAllowance > 0) {
                priceBreakdown += `
                <div class="amount-row">
                  <span>Driver Allowance</span>
                  <span>₹${driverAllowance.toLocaleString("en-IN")}</span>
                </div>`;
              }

              priceBreakdown += `
                <div class="amount-row">
                  <span>GST (${gstPercentage}%)</span>
                  <span>₹${taxAmount.toLocaleString("en-IN")}</span>
                </div>
                <div class="amount-row total-amount">
                  <span>Total Amount Paid</span>
                  <span>₹${totalAmount.toLocaleString("en-IN")}</span>
                </div>`;

              return priceBreakdown;
            })()}
          </div>

          <div class="amount-section" style="background: #f0f9ff; border: 1px solid #0ea5e9;">
            <div class="section-title" style="color: #0369a1;">Payment Information</div>
            <div class="amount-row">
              <span>Payment Method</span>
              <span>${booking.paymentMethod.toUpperCase()}</span>
            </div>
            <div class="amount-row">
              <span>Payment Status</span>
              <span style="color: ${booking.paymentStatus.toLowerCase() === 'paid' ? '#10b981' : '#ef4444'}; font-weight: bold;">${booking.paymentStatus.toUpperCase()}</span>
            </div>
            <div class="amount-row">
              <span>Transaction Date</span>
              <span>${booking.date}</span>
            </div>
          </div>

          <div class="footer">
            <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <p style="font-weight: bold; color: #374151; margin-bottom: 10px;">Tax Declaration:</p>
              <p style="font-size: 0.9rem; color: #6b7280;">This invoice shows the exact amount of tax (GST) charged as per Indian Tax Laws. GST is calculated on the base fare and additional charges as applicable.</p>
            </div>
            <div style="text-align: center;">
              <p style="font-size: 1.1rem; font-weight: bold; color: #10b981; margin-bottom: 15px;">Thank you for choosing UrbanRide!</p>
              <p style="margin-bottom: 5px;">This is a computer generated invoice and does not require signature.</p>
              <p style="margin-bottom: 5px;">For any queries, contact us at info@urbanridetech.com</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Handle reset corrupted rating
  const handleResetRating = async () => {
    try {
      if (!userInfo || !userInfo.token) {
        toast.error("You must be logged in to reset rating");
        return;
      }

      if (!rawBookingData || !rawBookingData._id) {
        toast.error("Booking information not available");
        return;
      }

      const response = await axios.delete(
        `${API_URL}/rating/driver/${rawBookingData._id}`,
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Rating reset successfully. You can now rate again.");
        // Refresh booking data
        setTimeout(() => {
          fetchBookingDetails();
        }, 500);
      } else {
        toast.error(response.data.message || "Failed to reset rating");
      }
    } catch (error) {
      console.error("Error resetting rating:", error);
      toast.error(error.response?.data?.message || "Failed to reset rating");
    }
  };

  // Handle rating submission
  const handleSubmitRating = async (ratingData) => {
    try {
      setIsSubmittingRating(true);

      if (!userInfo || !userInfo.token) {
        toast.error("You must be logged in to submit a rating");
        return;
      }

      if (!rawBookingData || !rawBookingData._id) {
        toast.error("Booking information not available");
        return;
      }

      // Enhanced driver ID extraction with detailed debugging
      console.log('🔍 DETAILED Driver Data Debug for Rating:', {
        rawBookingData: rawBookingData,
        driverSource: rawBookingData.driverSource,
        adminDriver: {
          value: rawBookingData.driver,
          type: typeof rawBookingData.driver,
          isString: typeof rawBookingData.driver === 'string',
          isObject: typeof rawBookingData.driver === 'object',
          hasId: rawBookingData.driver?._id,
          stringLength: typeof rawBookingData.driver === 'string' ? rawBookingData.driver.length : null
        },
        vendorDriver: {
          value: rawBookingData.vendorDriver,
          type: typeof rawBookingData.vendorDriver,
          isString: typeof rawBookingData.vendorDriver === 'string',
          isObject: typeof rawBookingData.vendorDriver === 'object',
          hasId: rawBookingData.vendorDriver?._id,
          stringLength: typeof rawBookingData.vendorDriver === 'string' ? rawBookingData.vendorDriver.length : null
        }
      });

      // Get the correct driver ID based on driver source with better handling
      let driverId = null;

      if (rawBookingData.driverSource === "vendor") {
        // Handle vendor driver
        if (rawBookingData.vendorDriver) {
          if (typeof rawBookingData.vendorDriver === 'object' && rawBookingData.vendorDriver._id) {
            driverId = rawBookingData.vendorDriver._id;
            console.log('✅ Using vendor driver object ID:', driverId);
          } else if (typeof rawBookingData.vendorDriver === 'string') {
            driverId = rawBookingData.vendorDriver;
            console.log('✅ Using vendor driver string ID:', driverId);
          }
        }
      } else {
        // Handle admin driver
        if (rawBookingData.driver) {
          if (typeof rawBookingData.driver === 'object' && rawBookingData.driver._id) {
            driverId = rawBookingData.driver._id;
            console.log('✅ Using admin driver object ID:', driverId);
          } else if (typeof rawBookingData.driver === 'string') {
            driverId = rawBookingData.driver;
            console.log('✅ Using admin driver string ID:', driverId);
          }
        }
      }

      console.log('🔍 Final Driver ID for rating:', {
        driverId,
        bookingId: rawBookingData._id,
        driverSource: rawBookingData.driverSource,
        extractionMethod: driverId ? 'success' : 'failed'
      });

      if (!driverId) {
        toast.error("No driver information available to rate");
        console.error('❌ No driver ID available for rating:', {
          driverSource: rawBookingData.driverSource,
          adminDriver: rawBookingData.driver,
          vendorDriver: rawBookingData.vendorDriver
        });
        return;
      }

      // Check if already rated before submitting
      if (hasUserRated()) {
        toast.error("You have already rated this driver");
        return;
      }

      // Validate rating value
      if (!ratingData.rating || ratingData.rating < 1 || ratingData.rating > 5) {
        toast.error("Please provide a valid rating between 1 and 5 stars");
        return;
      }

      const response = await ratingService.submitUserRating(
        rawBookingData._id,
        driverId,
        ratingData.rating,
        ratingData.comment || "",
        userInfo.token,
      );

      if (response.success) {
        toast.success("Rating submitted successfully");
        setShowRatingModal(false);
        // Force refresh booking data to get updated rating info
        setTimeout(() => {
          fetchBookingDetails();
        }, 500);
      } else {
        toast.error(response.message || "Failed to submit rating");
      }
    } catch (error) {
      console.error("Error submitting rating:", error);

      const errorMessage = error.response?.data?.message || error.message || "Failed to submit rating";

      // Log detailed error information for debugging
      console.error('Rating submission error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage,
        bookingId: rawBookingData?._id,
        driverId: rawBookingData?.driver,
        userInfo: userInfo ? { id: userInfo.id, hasToken: !!userInfo.token } : null
      });

      // Handle specific error cases
      if (errorMessage.includes("already rated")) {
        // Show a more helpful message with an option to reset
        const resetConfirm = window.confirm(
          "It seems you have already rated this driver, but there might be a data sync issue. Would you like to reset and try again?"
        );

        if (resetConfirm) {
          await handleResetRating();
        } else {
          toast.error("You have already rated this driver. Please refresh the page.");
          // Force refresh to sync the UI
          setTimeout(() => {
            fetchBookingDetails();
          }, 1000);
        }
      } else if (errorMessage.includes("not completed")) {
        toast.error("You can only rate completed rides");
      } else if (errorMessage.includes("driver mismatch")) {
        toast.error("Driver information doesn't match this booking");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Determine status color for badge
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "assigned":
      case "assigned_to_vendor":
        return "bg-indigo-100 text-indigo-800";
      case "inprogress":
      case "in progress":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return "Pending";
    const s = String(status).toLowerCase();
    if (s === "assigned_to_vendor") return "Assigned";
    // Humanize other statuses
    const human = String(status).replace(/_/g, " ");
    return human.charAt(0).toUpperCase() + human.slice(1);
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Booking Details</h1>
        <div className="flex gap-2">

          <button
            onClick={() => navigate("/")}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
          >
            <FaArrowRight className="mr-2 transform rotate-180" />
            Back to Home
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="border-b pb-4 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <h2 className="text-2xl font-semibold">Booking #{booking.id}</h2>
              {/* Service type, trip type, and cab type indicators */}
              <div className="flex flex-wrap gap-2 mt-2 items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  booking.serviceType === "rental"
                    ? "bg-orange-100 text-orange-800"
                    : booking.serviceType === "airport"
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}>
                  {booking.serviceType === "rental" ? "Local Rental" :
                   booking.serviceType === "airport" ? "Airport Transfer" : "Outstation"}
                </span>
                {booking.serviceType === "outstation" && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    booking.tripType === "roundTrip"
                      ? "bg-purple-100 text-purple-800"
                      : booking.viaCities && booking.viaCities.length > 0
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}>
                    {(() => {
                      if (booking.tripType === "roundTrip") {
                        return "Round Trip";
                      } else if (booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)) {
                        // Get via cities from booking data or URL params
                        const viaCities = booking.viaCities || (viaCitiesParam
                          ? viaCitiesParam.split(",").map(city => city.trim()).filter(city => city.length > 0)
                          : []);
                        return viaCities.length > 0
                          ? `Multi-City (via ${viaCities.join(", ")})`
                          : "Multi-City";
                      } else {
                        return "One Way";
                      }
                    })()}
                  </span>
                )}
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800 flex items-center gap-2">
                  <FaCar className="text-indigo-600" />
                  {String(booking.cabType || cabNameParam || "Standard")}
                </span>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded font-medium ${getStatusColor(booking.status)}`}
            >
              {getStatusLabel(booking.status)}
            </span>
          </div>
          <p className="text-gray-600">
            Booked for {String(booking.date || "Date not specified")},{" "}
            {booking.time instanceof Date
              ? convertTo12HourFormat(booking.time.toLocaleTimeString())
              : convertTo12HourFormat(String(booking.time || "Time not specified"))}
          </p>
        </div>

        {/* Trip Details Summary Component - Only show when via cities are present */}
        {(() => {
          const viaCities = viaCitiesParam
            ? viaCitiesParam
                .split(",")
                .map((city) => city.trim())
                .filter((city) => city.length > 0)
            : booking?.viaCities || [];

          return viaCities.length > 0 ? (
            <TripDetailsSummary
              startLocation={
                cleanLocationString(
                  booking?.dbFrom || booking?.from || fromParam,
                ) || "Origin not specified"
              }
              destination={
                cleanLocationString(booking?.dbTo || booking?.to || toParam) ||
                "Destination not specified"
              }
              viaCities={viaCities}
              totalDistance={
                parseFloat(booking?.distance || distanceParam) || 0
              }
              baseRate={
                parseFloat(booking?.baseRate || booking?.pricePerKm) || 12
              }
              totalFare={
                parseFloat(
                  booking?.totalAmount || booking?.price || amountParam,
                ) || 0
              }
              tripType={(() => {
                if (booking?.tripType === "roundTrip") {
                  return "Round Trip";
                } else if (booking?.tripType === "multiCity" || (booking?.viaCities && booking?.viaCities.length > 0)) {
                  const viaCities = booking?.viaCities || (viaCitiesParam
                    ? viaCitiesParam.split(",").map(city => city.trim()).filter(city => city.length > 0)
                    : []);
                  return viaCities.length > 0
                    ? `Multi-City (via ${viaCities.join(", ")})`
                    : "Multi-City";
                } else {
                  return "One Way";
                }
              })()}
              cabType={booking?.cabType || cabNameParam || "Standard"}
              className="mb-6"
            />
          ) : null;
        })()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium mb-3">
              Additional Trip Information
            </h3>

            {/* Trip Start OTP Display (hidden when ride is completed) */}
            {booking.startOtp && String(booking.status).toLowerCase() !== "completed" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                  <span className="mr-2">🔐</span>
                  Trip Start OTP
                </h4>
                <div className="bg-white border-2 border-green-300 rounded-md p-3 text-center">
                  <p className="text-2xl font-bold text-green-700 tracking-widest">
                    {booking.startOtp}
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    Share this OTP with your driver before trip starts
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="space-y-3">
                {/* Route Information */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <FaRoute className="mr-2" />
                    Route Information
                  </h4>

                  <div className="space-y-3">
                    {/* For rental services, show package details instead of pickup/drop */}
                    {booking.serviceType === "rental" &&
                    (booking.packageName ||
                      booking.rentalHours ||
                      booking.rentalKm) ? (
                      <div className="bg-white p-3 rounded border">
                        <div className="flex items-start">
                          <div className="bg-orange-100 p-1.5 rounded-full mr-3 mt-0.5">
                            <FaCar className="text-orange-500" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {booking.packageName
                                ? `Rental Package: ${booking.packageName}`
                                : "Rental Service"}
                            </p>
                            <div className="mt-2 space-y-1">
                              {booking.rentalHours && (
                                <p className="text-sm text-gray-600">
                                  Duration: {booking.rentalHours} hours
                                </p>
                              )}
                              {booking.rentalKm && (
                                <p className="text-sm text-gray-600">
                                  Included Distance: {booking.rentalKm} km
                                </p>
                              )}
                            </div>
                            <div className="mt-3 space-y-2">
                              <div className="bg-blue-50 p-3 rounded border border-blue-100">
                                <p className="font-medium text-blue-800 mb-2 flex items-center">
                                  <FaMapMarkerAlt className="mr-2" />
                                  Pickup Location Details
                                </p>
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-900 font-medium">
                                    {(() => {
                                      // Handle ObjectId case for rental package location - show only one city name
                                      const locationOptions = [
                                        booking.dbFrom,
                                        booking.from,
                                        fromParam,
                                      ];

                                      for (const location of locationOptions) {
                                        if (
                                          location &&
                                          typeof location === "string"
                                        ) {
                                          // Check if it's an ObjectId (24 character hex string)
                                          if (
                                            location.length === 24 &&
                                            /^[0-9a-fA-F]{24}$/.test(location)
                                          ) {
                                            continue; // Skip ObjectIds
                                          }
                                          return location;
                                        }
                                      }
                                      return "Location not specified";
                                    })()}
                                  </p>
                                  {(() => {
                                    // Show pickup address details for rental packages
                                    const pickupAddr = cleanLocationString(
                                      booking.pickupAddress || queryParams.get("pickupAddress")
                                    );
                                    const pickupLandmark = booking.pickupAddressDetails?.landmark ||
                                      queryParams.get("pickupLandmark");
                                    const pickupPincode = booking.pickupAddressDetails?.pincode ||
                                      queryParams.get("pickupPincode");

                                    if (pickupAddr || pickupLandmark || pickupPincode) {
                                      return (
                                        <div className="space-y-1">
                                          {pickupAddr && (
                                            <p className="text-sm text-gray-600">
                                              <span className="font-medium">Address:</span> {pickupAddr}
                                            </p>
                                          )}
                                          {pickupLandmark && (
                                            <p className="text-sm text-gray-600">
                                              <span className="font-medium">Landmark:</span> {pickupLandmark}
                                            </p>
                                          )}
                                          {pickupPincode && (
                                            <p className="text-sm text-gray-600">
                                              <span className="font-medium">Pincode:</span> {pickupPincode}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    return (
                                      <p className="text-sm text-gray-500 italic">
                                        Detailed pickup address will be provided by the customer
                                      </p>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Check if pickup and drop are the same for non-rental but same city bookings */}
                        {(() => {
                          const cleanFrom = cleanLocationString(
                            booking.dbFrom || booking.from || fromParam,
                          );
                          const cleanTo = cleanLocationString(
                            booking.dbTo || booking.to || toParam,
                          );

                          // If both locations are the same and not ObjectIds, show as single location
                          if (
                            cleanFrom &&
                            cleanTo &&
                            cleanFrom.toLowerCase() === cleanTo.toLowerCase()
                          ) {
                            return (
                              <div className="bg-white p-3 rounded border">
                                <div className="flex items-start">
                                  <div className="bg-blue-100 p-1.5 rounded-full mr-3 mt-0.5">
                                    <FaCar className="text-blue-500" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      Local Service in {cleanFrom}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Pickup and drop-off in the same city
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // Otherwise show separate pickup and drop locations
                          return (
                            <>
                              {/* Pickup Location */}
                              <div className="bg-white p-3 rounded border">
                                <div className="flex items-start">
                                  <div className="bg-red-100 p-1.5 rounded-full mr-3 mt-0.5">
                                    <FaMapMarkerAlt className="text-red-500" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {cleanFrom ||
                                        "Pickup address not specified"}
                                    </p>
                                    {(() => {
                                      // Show pickup address from URL params or database
                                      const mainLocation = cleanFrom;
                                      const pickupAddr =
                                        cleanLocationString(
                                          booking.pickupAddress,
                                        ) || cleanLocationString(pickupAddress); // Include URL param address
                                      const pickupLandmark =
                                        booking.pickupAddressDetails?.landmark;

                                      // Always show detailed address if available
                                      if (pickupAddr) {
                                        return (
                                          <p className="text-sm text-gray-600 mt-1">
                                            {pickupAddr}
                                            {pickupLandmark &&
                                              `, Near ${pickupLandmark}`}
                                          </p>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                              </div>

                              {/* Drop-off Location */}
                              <div className="bg-white p-3 rounded border">
                                <div className="flex items-start">
                                  <div className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5">
                                    <FaMapMarkerAlt className="text-green-500" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {cleanTo ||
                                        "Drop-off address not specified"}
                                    </p>
                                    {(() => {
                                      // Show drop address from URL params or database
                                      const mainLocation = cleanTo;
                                      const dropAddr =
                                        cleanLocationString(
                                          booking.dropAddress,
                                        ) || cleanLocationString(dropAddress); // Include URL param address
                                      const dropLandmark =
                                        booking.dropAddressDetails?.landmark;

                                      // Always show detailed address if available
                                      if (dropAddr) {
                                        return (
                                          <p className="text-sm text-gray-600 mt-1">
                                            {dropAddr}
                                            {dropLandmark &&
                                              `, Near ${dropLandmark}`}
                                          </p>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {/* Additional Trip Details */}
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                  {/* For rental services, don't show duplicate package details - they're already shown above */}
                  {booking.serviceType !== "rental" ? (
                    <>
                      <div>
                        <p className="font-medium text-gray-600">Distance</p>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">Distance:</span>
                          <span className="font-medium">
                            {(() => {
                              // Use the most accurate distance source, prioritizing known route distances
                              const knownDistance = distanceParam;
                              const bookingDistance = booking.distance;
                              const rawDistance = rawBookingData?.distance;

                              // For Rajkot-Dehradun route, ensure we use the correct distance
                              if ((booking.from?.toLowerCase().includes('rajkot') && booking.to?.toLowerCase().includes('dehradun')) ||
                                  (booking.from?.toLowerCase().includes('dehradun') && booking.to?.toLowerCase().includes('rajkot'))) {
                                return "1,383 km";
                              }

                              const distance = knownDistance || rawDistance || bookingDistance;
                              return formatDistance(distance);
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Estimated Time:</span>
                          <span className="font-medium">
                            {(() => {
                              // Use the same distance logic for consistent travel time calculation
                              if ((booking.from?.toLowerCase().includes('rajkot') && booking.to?.toLowerCase().includes('dehradun')) ||
                                  (booking.from?.toLowerCase().includes('dehradun') && booking.to?.toLowerCase().includes('rajkot'))) {
                                return formatTravelTime(1383);
                              }

                              const knownDistance = distanceParam;
                              const bookingDistance = booking.distance;
                              const rawDistance = rawBookingData?.distance;
                              const distance = knownDistance || rawDistance || bookingDistance;
                              return formatTravelTime(distance);
                            })()}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Cab Type</p>
                        <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <FaCar className="text-indigo-600" />
                          <span className="font-semibold">{String(booking.cabType || "Standard")}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-2">
                      <p className="font-medium text-gray-600">Cab Type</p>
                      <div className="mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                        <FaCar className="text-indigo-600" />
                        <span className="font-semibold">{String(booking.cabType || "Standard")}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Round Trip Details */}
                {booking.tripType === "roundTrip" &&
                  (booking.returnDate || booking.returnTime) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                          <FaArrowRight className="mr-2 transform rotate-90" />
                          Round Trip Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {booking.returnDate && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 font-medium">
                                Return Date:
                              </span>
                              <span className="font-semibold text-blue-700">
                                {formatDateDDMMYYYY(booking.returnDate)}
                              </span>
                            </div>
                          )}
                          {booking.returnTime && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 font-medium">
                                Return Time:
                              </span>
                              <span className="font-semibold text-blue-700">
                                {booking.returnTime}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-3">Payment Details</h3>
            <div className="space-y-2">
              {/* Price Breakdown */}
              {(() => {
                const parseCurrency = (val) => {
                  if (val == null) return 0;
                  if (typeof val === 'number') return val;
                  const n = String(val).replace(/[^0-9.]/g, '');
                  return Number(n) || 0;
                };
                const total = parseCurrency(rawBookingData?.totalAmount ?? booking.price);
                const base = rawBookingData?.baseAmount != null ? Number(rawBookingData.baseAmount) : Math.round(total / 1.18);
                const tax = rawBookingData?.taxAmount != null ? Number(rawBookingData.taxAmount) : Math.max(0, total - base);
                const gstPct = base > 0 ? Math.round((tax / base) * 100) : 18;
                const formatINR = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.max(0, Math.round(amt)));
                if (!total) return null;
                return (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Actual Price</span>
                      <span className="font-medium text-gray-900">{formatINR(base)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-700">GST ({gstPct}%)</span>
                      <span className="font-medium text-gray-900">{formatINR(tax)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200">
                      <span className="text-gray-800 font-semibold">Total Amount</span>
                      <span className="font-semibold text-gray-900">{formatINR(total)}</span>
                    </div>
                  </div>
                );
              })()}

              <div
                className={`p-3 rounded-lg border ${getPaymentMethodBadgeClass(booking.paymentMethod).includes("blue") ? "bg-blue-50 border-blue-200" : "bg-yellow-50 border-yellow-200"}`}
              >
                <p
                  className={`font-medium ${getPaymentMethodBadgeClass(booking.paymentMethod).includes("blue") ? "text-blue-800" : "text-yellow-800"}`}
                >
                  {getPaymentMethodDisplay(booking.paymentMethod)}
                </p>
                <p
                  className={`font-medium mt-1 ${getPaymentStatusBadgeClass(booking).includes("green") ? "text-green-600" : "text-orange-600"}`}
                >
                  {getPaymentStatusDisplay(booking)}
                </p>
                {booking.paymentId && (booking.paymentMethod === "ONLINE" || booking.paymentMethod === "Online Payment" || booking.paymentMethod === "online") && (
                  <p className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">Payment ID:</span> {booking.paymentId}
                  </p>
                )}
              </div>
            </div>

            <h3 className="text-lg font-medium mt-6 mb-3">Driver Details</h3>
            <div className="space-y-2">
              {(() => {
                // Check for both admin driver and vendor driver
                const driverInfo = booking.driver || booking.vendorDriver;
                const driverType = booking.driver ? "Admin Driver" : booking.vendorDriver ? "Vendor Driver" : null;

                return (
                  <>
                    <p>
                      <span className="font-medium">Name:</span>{" "}
                      {String(driverInfo?.name || booking.driverName || "To be assigned")}
                    </p>
                    <p>
                      <span className="font-medium">Contact:</span>{" "}
                      {String(driverInfo?.phone || booking.driverContact || "To be provided")}
                    </p>
                    {driverType && (
                      <p>
                        <span className="font-medium">Driver Type:</span>{" "}
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          driverType === "Admin Driver" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                        }`}>
                          {driverType}
                        </span>
                      </p>
                    )}
                    {(driverInfo?.vehicleModel || booking.driverVehicle) && (
                      <p>
                        <span className="font-medium">Vehicle:</span>{" "}
                        {String(driverInfo?.vehicleModel || booking.driverVehicle || "Not assigned")}
                      </p>
                    )}
                    {(driverInfo?.vehicleNumber) && (
                      <p>
                        <span className="font-medium">Vehicle Number:</span>{" "}
                        {String(driverInfo?.vehicleNumber || "Not provided")}
                      </p>
                    )}
                    {(driverInfo?.licenseNumber || booking.driverLicense) && (
                      <p>
                        <span className="font-medium">License:</span>{" "}
                        {String(driverInfo?.licenseNumber || booking.driverLicense || "Not provided")}
                      </p>
                    )}
                  </>
                );
              })()}
              {booking.status === "assigned" && (
                <div className="mt-2 py-1 px-2 bg-green-100 text-green-800 rounded-md inline-block">
                  Driver assigned and ready
                </div>
              )}

              {/* Display driver rating if available */}
              {(() => {
                // Check for driver rating in different data structures
                const driverRating = rawBookingData?.driverRating || rawBookingData?.ratings?.driver;

                // Check driver rating display

                // Simplified condition - just check if driverRating exists and has a rating property
                if (driverRating && driverRating.rating) {
                  return (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="font-medium text-yellow-800 mb-1">
                        Driver's Rating for You
                      </p>
                      <div className="flex items-center">
                        <div className="flex text-yellow-500 mr-2">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={
                                i < Number(driverRating.rating || 0)
                                  ? "text-yellow-500"
                                  : "text-gray-300"
                              }
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          (
                          {isNaN(Number(driverRating.rating))
                            ? 0
                            : Number(driverRating.rating)}
                          /5)
                        </span>
                      </div>
                      {driverRating.comment && (
                        <p className="text-sm text-gray-700 mt-2 italic">
                          "{driverRating.comment}"
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Display user rating for driver if available */}
              {(() => {
                // Check for user rating in different data structures
                const userRating = rawBookingData?.userRating || rawBookingData?.ratings?.user;

                console.log('🔍 User rating display check:', {
                  userRating,
                  hasRating: userRating?.rating,
                  ratingValue: userRating?.rating,
                  ratingType: typeof userRating?.rating
                });

                // Display user's rating for the driver if it exists
                if (userRating && (userRating.rating !== undefined && userRating.rating !== null && userRating.rating !== '')) {
                  console.log('✅ Displaying user rating:', userRating.rating);
                  return (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-medium text-blue-800 mb-1">
                        Your Rating for Driver
                      </p>
                      <div className="flex items-center">
                        <div className="flex text-blue-500 mr-2">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={
                                i < Number(userRating.rating || 0)
                                  ? "text-blue-500"
                                  : "text-gray-300"
                              }
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          (
                          {isNaN(Number(userRating.rating))
                            ? 0
                            : Number(userRating.rating)}
                          /5)
                        </span>
                      </div>
                      {userRating.comment && (
                        <p className="text-sm text-gray-700 mt-2 italic">
                          "{userRating.comment}"
                        </p>
                      )}
                    </div>
                  );
                } else {
                  console.log('❌ User rating not displayed - no valid rating found');
                }
                return null;
              })()}

            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t flex justify-end gap-2">


          {booking.status === "completed" && (
            <button
              onClick={handleDownloadInvoice}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              <span>Download Invoice</span>
            </button>
          )}

          {/* Show Rate Driver button only for completed rides where user hasn't rated yet */}
          {(() => {
            // Check conditions for showing the rating button
            const isCompleted = (rawBookingData && rawBookingData.status === "completed") ||
                               (booking && booking.status === "completed");

            // Check if we have driver information from either admin or vendor driver
            const hasDriverInfo = !!(booking.driverName && booking.driverName !== "To be assigned") ||
                                !!(rawBookingData && (rawBookingData.driver || rawBookingData.vendorDriver));

            const userLoggedIn = !!userInfo;
            const hasAlreadyRated = hasUserRated();

            console.log('🔍 Rating button conditions debug:', {
              isCompleted,
              hasDriverInfo,
              userLoggedIn,
              hasAlreadyRated,
              driverName: booking.driverName,
              rawHasAdminDriver: !!(rawBookingData && rawBookingData.driver),
              rawHasVendorDriver: !!(rawBookingData && rawBookingData.vendorDriver),
              shouldShow: isCompleted && hasDriverInfo && userLoggedIn && !hasAlreadyRated
            });

            // Only show the button if:
            // 1. Ride is completed
            // 2. Driver information is available
            // 3. User is logged in
            // 4. User has NOT already rated
            const shouldShowButton = isCompleted && hasDriverInfo && userLoggedIn && !hasAlreadyRated;

            return shouldShowButton ? (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowRatingModal(true)}
                  className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                >
                  Rate Your Driver
                </button>

              </div>
            ) : null;
          })()}

          {booking.canCancel && (
            <button
              onClick={() => setShowCancellationModal(true)}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              disabled={isCancelling}
            >
              {isCancelling ? "Processing..." : "Cancel Booking"}
            </button>
          )}
        </div>

        {/* Cancellation Modal */}
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onConfirm={handleCancelBooking}
          booking={rawBookingData}
        />

        {/* Rating Modal */}
        {rawBookingData?._id && (
          <RatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            onSubmit={handleSubmitRating}
            recipientName={booking.driverName}
            recipientType="driver"
            bookingId={rawBookingData._id}
          />
        )}
      </div>
    </div>
  );
};

export default BookingDetails;
